import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  FormHelperText,
} from '@mui/material'
import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCreateAnnouncementMutation } from 'services/announcements'
import { v4 as uuidv4 } from 'uuid'
import { createAnnouncementSchema } from './validations'

type FormFields = {
  announcementText: string
}

type Props = {
  tenderId: string
}

export const CreateAnouncement = ({ tenderId }: Props) => {
  const { t } = useTranslation()
  const schema = createAnnouncementSchema(t)

  const [createAnnouncement, { isSuccess, isError }] =
    useCreateAnnouncementMutation()

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormFields>({
    defaultValues: {
      announcementText: '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  const onSubmit = ({ announcementText }: FormFields) => {
    createAnnouncement({
      id: uuidv4(),
      announcementText,
      tenderId,
    })
    reset()
  }

  return (
    <Stack spacing={1}>
      <Typography variant="h6" fontWeight="bold">
        {t('informingAllSuppliers')}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Controller
          name="announcementText"
          control={control}
          render={({ field }) => (
            <Box width="100%">
              <TextField
                {...field}
                label={t('insertInformingAllSuppliersText')}
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
              />
              {errors.announcementText && (
                <FormHelperText error>
                  {errors.announcementText?.message}
                </FormHelperText>
              )}
            </Box>
          )}
        />
        <Button
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
        >
          {t('send')}
        </Button>
      </Stack>
    </Stack>
  )
}
